import React, { useMemo, useRef, useState } from 'react';
import { parseSpreadsheetFile, guessHeader, ParsedSheet } from './importUtils.ts';

export interface ImportFieldDef {
  key: string;
  label: string;
  required: boolean;
  aliases: string[];
  hint?: string;
}

export type RowOutcome<T> = { ok: true; value: T } | { ok: false; error: string };

interface SpreadsheetImportProps<T> {
  title: string;
  description: string;
  fields: ImportFieldDef[];
  // Turn one mapped row (keyed by field key) into a value to create, or an error to show.
  transformRow: (mapped: Record<string, string>, rowIndex: number) => RowOutcome<T>;
  // Persist the valid rows. Should resolve to a human-readable success message.
  onCommit: (values: T[]) => Promise<string>;
  // Optional controls (e.g. a date picker) rendered above the file chooser.
  extraControls?: React.ReactNode;
  // Gate preview/commit until prerequisites are ready (date chosen, reference data loaded).
  ready?: boolean;
  notReadyMessage?: string;
}

const PREVIEW_LIMIT = 50;

function SpreadsheetImport<T>(props: SpreadsheetImportProps<T>) {
  const { title, description, fields, transformRow, onCommit, extraControls, ready = true, notReadyMessage } = props;

  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [parseError, setParseError] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setResult(null);
    setParseError('');
    setShowAll(false);
    try {
      const sheet = await parseSpreadsheetFile(file);
      if (sheet.headers.length === 0 || sheet.rows.length === 0) {
        setParsed(null);
        setParseError('That file had no readable rows. Make sure the first row is column headers.');
        return;
      }
      setFileName(file.name);
      setParsed(sheet);
      // Auto-guess a starting column mapping from the field aliases.
      const guessed: Record<string, string> = {};
      fields.forEach(f => { guessed[f.key] = guessHeader(sheet.headers, [f.label, ...f.aliases]); });
      setMapping(guessed);
    } catch (err) {
      console.error('Spreadsheet parse failed:', err);
      setParsed(null);
      setParseError('Could not read that file. Supported formats: Excel (.xlsx, .xls) and CSV (.csv).');
    }
  };

  const requiredMapped = fields.filter(f => f.required).every(f => mapping[f.key]);

  // Build the per-row preview: mapped values + ok/error outcome.
  const preview = useMemo(() => {
    if (!parsed || !requiredMapped) return [] as { index: number; mapped: Record<string, string>; outcome: RowOutcome<T> }[];
    return parsed.rows.map((row, index) => {
      const mapped: Record<string, string> = {};
      fields.forEach(f => { mapped[f.key] = mapping[f.key] ? (row[mapping[f.key]] ?? '') : ''; });
      return { index, mapped, outcome: transformRow(mapped, index) };
    });
  }, [parsed, mapping, requiredMapped, fields, transformRow]);

  const validValues = useMemo(
    () => preview.filter(p => p.outcome.ok).map(p => (p.outcome as { ok: true; value: T }).value),
    [preview]
  );
  const errorCount = preview.length - validValues.length;

  const handleCommit = async () => {
    if (validValues.length === 0) return;
    setCommitting(true);
    setResult(null);
    try {
      const msg = await onCommit(validValues);
      setResult({ type: 'success', text: msg });
      setParsed(null);
      setFileName('');
      setMapping({});
    } catch (err: any) {
      setResult({ type: 'error', text: err?.response?.data?.error || err?.message || 'Import failed.' });
    } finally {
      setCommitting(false);
    }
  };

  const rowsToShow = showAll ? preview : preview.slice(0, PREVIEW_LIMIT);

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p style={{ fontSize: '0.9em', color: 'var(--text-muted)', marginBottom: '12px' }}>{description}</p>

      {extraControls && <div style={{ marginBottom: '12px' }}>{extraControls}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="btn btn-primary"
          style={{ padding: '8px 16px' }}
        >
          Choose Excel / CSV file…
        </button>
        {fileName && <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>{fileName}</span>}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      </div>

      {parseError && <div className="alert alert-error" style={{ marginTop: '10px' }}>{parseError}</div>}

      {parsed && (
        <>
          {/* Column mapping */}
          <div style={{ marginTop: '14px' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>Match your columns</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '10px' }}>
              {fields.map(f => {
                const unmappedRequired = f.required && !mapping[f.key];
                return (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: '0.82em', marginBottom: '3px' }}>
                      {f.label}{f.required && <span style={{ color: 'var(--danger, #c23b3b)' }}> *</span>}
                    </label>
                    <select
                      className="form-control"
                      value={mapping[f.key] || ''}
                      onChange={(e) => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                      style={unmappedRequired ? { borderColor: 'var(--danger, #c23b3b)' } : undefined}
                    >
                      <option value="">— not in file —</option>
                      {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {f.hint && <div style={{ fontSize: '0.72em', color: 'var(--text-muted)', marginTop: '2px' }}>{f.hint}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {!requiredMapped && (
            <div className="alert alert-info" style={{ marginTop: '12px' }}>
              Match the required columns (marked <span style={{ color: 'var(--danger, #c23b3b)' }}>*</span>) to preview the import.
            </div>
          )}

          {requiredMapped && !ready && notReadyMessage && (
            <div className="alert alert-info" style={{ marginTop: '12px' }}>{notReadyMessage}</div>
          )}

          {requiredMapped && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <span style={{ fontWeight: 600 }}>{preview.length} rows</span>
                <span style={{ color: 'var(--success-text, #16a34a)' }}>✓ {validValues.length} ready</span>
                {errorCount > 0 && <span style={{ color: 'var(--warning-text, #d97706)' }}>⚠ {errorCount} need attention (will be skipped)</span>}
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid var(--border-color, rgba(128,128,128,0.3))', borderRadius: 'var(--radius-md, 6px)' }}>
                <table className="table" style={{ margin: 0, minWidth: '640px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      {fields.map(f => <th key={f.key}>{f.label}</th>)}
                      <th style={{ width: '200px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsToShow.map(p => (
                      <tr key={p.index} style={p.outcome.ok ? undefined : { background: 'rgba(217,119,6,0.12)' }}>
                        <td style={{ color: 'var(--text-muted)' }}>{p.index + 1}</td>
                        {fields.map(f => <td key={f.key} style={{ fontSize: '0.85em' }}>{p.mapped[f.key] || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>)}
                        <td style={{ fontSize: '0.82em' }}>
                          {p.outcome.ok
                            ? <span style={{ color: 'var(--success-text, #16a34a)' }}>✓ ready</span>
                            : <span style={{ color: 'var(--warning-text, #d97706)' }}>⚠ {(p.outcome as { ok: false; error: string }).error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {preview.length > PREVIEW_LIMIT && (
                <button
                  type="button"
                  onClick={() => setShowAll(s => !s)}
                  style={{ marginTop: '8px', background: 'transparent', border: 'none', color: 'var(--or-blue-mid)', cursor: 'pointer', fontSize: '0.85em' }}
                >
                  {showAll ? 'Show fewer' : `Show all ${preview.length} rows`}
                </button>
              )}

              <div style={{ marginTop: '14px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={committing || validValues.length === 0 || !ready}
                  onClick={handleCommit}
                  style={{ padding: '9px 18px' }}
                >
                  {committing ? 'Importing…' : `Import ${validValues.length} row${validValues.length === 1 ? '' : 's'}`}
                </button>
                {errorCount > 0 && (
                  <span style={{ marginLeft: '10px', fontSize: '0.82em', color: 'var(--text-muted)' }}>
                    Rows with a ⚠ are skipped — fix them in your spreadsheet and re-upload to add them.
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {result && (
        <div className={`alert alert-${result.type === 'error' ? 'error' : 'success'}`} style={{ marginTop: '14px' }}>
          {result.text}
        </div>
      )}
    </div>
  );
}

export default SpreadsheetImport;
